import { Box, Stack, Typography, Tooltip, Chip } from '@mui/material';
import { ArrowRight2, SearchNormal1, Send2 } from 'iconsax-reactjs';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import CustomTable from 'components/@extended/CustomTable';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import SkeletonTable from 'components/SkeletonTable';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { getAssessorWiseDetails } from 'services/assessorDetails.service';
import TextField from '@mui/material/TextField';
import useAuth from 'hooks/useAuth';

interface BatchInfo {
  _id: string;
  name: string;
}

interface QualificationPack {
  _id: string;
  qpCode: string;
  qpName: string;
  nsqfLevel?: number;
}

interface Nos {
  _id: string;
  nosCode: string;
  nosName: string;
  totalMarks: number;
}

interface Pc {
  _id: string;
  pcCode: string;
  pcName: string;
  totalMarks: number;
  isCritical: boolean;
  type: string;
}

interface AssessorTemplate {
  _id: string;
  templateName: string;
  startDate: string;
  endDate: string;
  batch: BatchInfo;
  qualificationPack: QualificationPack;
  nos: Nos[];
  pcs: Pc[];
  type: string;
  attemptNumber?: number;
  resultsReleased?: boolean;
  sessionStatus?: string;
}

const formatDate = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const sessionDisplayStatus = (row: Pick<AssessorTemplate, 'resultsReleased' | 'endDate' | 'sessionStatus'>) => {
  if (row.resultsReleased) return 'Completed';
  const end = row.endDate ? new Date(row.endDate) : null;
  if (end && !Number.isNaN(end.getTime()) && end.getTime() < Date.now()) return 'Overdue';
  return 'Pending';
};

const PerformanceEvaluation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AssessorTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sort, setSort] = useState<Array<{ id: string; sort?: boolean; desc?: boolean }>>([]);
  const [total, setTotal] = useState(0);
  const [initialTableReady, setInitialTableReady] = useState(false);

  const handleSearchInputChange = useCallback((value: string) => {
    setInputValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPageIndex(1);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      const activeSortItem = sort?.[0]; // get first item from array
      const sortColumn = activeSortItem?.id;
      const sortOrder = activeSortItem ? (activeSortItem.desc ? 'desc' : 'asc') : undefined;
      const query = {
        page: pageIndex,
        limit: pageSize,
        search: search || undefined,
        sortColumn,
        sortOrder
      };
      try {
        const res = await getAssessorWiseDetails(query);
        setTemplates(res?.data?.data?.data || []);
        setTotal(res?.data?.data?.total || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setInitialTableReady(true);
      }
    };
    fetchTemplates();
  }, [pageIndex, pageSize, search, sort]);

  const columns = useMemo(
    () => [
      {
        id: 'batchName',
        header: 'BATCH NAME',
        accessorKey: 'batch.name',
        cell: ({ row }: any) => <Typography>{row.original.batch?.name || '-'}</Typography>
      },
      {
        id: 'templateName',
        header: 'TEMPLATE',
        accessorKey: 'templateName',
        cell: ({ row }: any) => <Typography> {row.original.templateName || '-'} </Typography>
      },
      {
        id: 'qualificationPack',
        header: 'QUALIFICATION PACK',
        accessorKey: 'qualificationPack.qpName',
        cell: ({ row }: any) => <Typography> {row.original.qualificationPack?.qpName || '-'} </Typography>
      },
      {
        id: 'nos',
        header: 'NOS',
        enableSorting: false,
        cell: ({ row }: any) => {
          const nosList: Nos[] = row.original.nos || [];
          if (!nosList.length)
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          const first = nosList[0];
          const displayText = nosList.length === 1 ? `${first.nosCode} — ${first.nosName}` : `${first.nosCode} — ${first.nosName} ...`;
          const tooltipContent = (
            <Stack spacing={0.5} sx={{ py: 0.5 }}>
              {nosList.map((n) => (
                <Typography key={n._id} variant="body2">
                  {`${n.nosCode} — ${n.nosName}`}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    (Marks: {n.totalMarks})
                  </Typography>
                </Typography>
              ))}
            </Stack>
          );
          return (
            <Tooltip title={tooltipContent} placement="bottom" arrow>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  cursor: 'help'
                }}
              >
                {displayText}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        cell: ({ row }: any) => {
          return row.original.type.replace(/^./, (char : any) => char.toUpperCase());
        }
      },
      ...(user?.type === 'assessorAdmin'
        ? [
            {
              id: 'sessionStatus',
              header: 'STATUS',
              enableSorting: false,
              cell: ({ row }: any) => {
                const label = sessionDisplayStatus(row.original);
                const color =
                  label === 'Completed' ? 'success' : label === 'Overdue' ? 'error' : 'warning';
                return <Chip label={label} size="small" color={color as any} variant="outlined" sx={{ fontWeight: 600 }} />;
              }
            }
          ]
        : []),
      {
        id: 'attemptNumber',
        header: 'Attempt',
        accessorKey: 'attemptNumber',
        cell: ({ row }: any) => <Typography> {row.original.attemptNumber || 1} </Typography>
      },
      {
        id: 'actions',
        header: 'OPTIONS',
        enableSorting: false,
        cell: ({ row }: any) => (
          <Tooltip title="View batch students and status">
            <IconButton
              color="primary"
              onClick={() =>
                navigate('/performance-evaluation/batch-students-marks', {
                  state: {
                    batchId: row.original.batch?._id,
                    assessmentSessionId: row.original._id
                  }
                })
              }
            >
              <Send2 />
            </IconButton>
          </Tooltip>
        )
      }
    ],
    [navigate, user?.type]
  );

  const handleSortingData = useCallback((payload: { name: string }) => {
    setSort((prev) => {
      const existing = prev.find((s) => s.id === payload.name);

      if (!existing) {
        return [{ id: payload.name, sort: true, desc: false }];
      }
      if (existing.sort && !existing.desc) {
        return [{ id: payload.name, sort: true, desc: true }];
      }
      if (existing.sort && existing.desc) {
        return [];
      }

      return prev;
    });

    setPageIndex(1);
  }, []);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show />
            <Box>
              <Typography variant="h5">
                <FormattedMessage id="Performance Evaluation" />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="Manage the performance evaluation for students." />
              </Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 2 }}>
            <TextField
              placeholder="Search batch or template..."
              size="small"
              value={inputValue}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setPageIndex(1);
              }}
              // sx={{ width: 240 }}
            />
            <IconButton variant="outlined" onClick={() => setPageIndex(1)}>
              <SearchNormal1 />
            </IconButton>
          </Stack>
        }
      />

      <MainCard content={false}>
        {!initialTableReady && loading ? (
          <SkeletonTable columns={columns} />
        ) : (
          <CustomTable
            columns={columns}
            data={templates}
            sortArray={['batchName', 'templateName', 'startDate', 'endDate']}
            sort={sort}
            setSortingData={handleSortingData}
            striped
            count={total}
            size={pageSize}
            pageIndex={pageIndex}
            handlePageChange={(_event: unknown, page: number) => setPageIndex(page)}
            handleSizeChange={(e: any) => {
              setPageSize(Number(e.target.value));
              setPageIndex(1);
            }}
            notFound={total === 0 && !loading}
            search={search}
            loading={initialTableReady && loading}
          />
        )}
      </MainCard>
    </Box>
  );
};

export default PerformanceEvaluation;
